import React from "react";
import { useSeasonPassFlag } from "../hooks/useSeasonPassFlag";

/**
 * SeasonPassBanner — themed promo banner driven entirely by the admin
 * editor in /admin → SEASON PASS. Renders nothing when the platform flag
 * is OFF (the default).
 *
 * Theme colours come from `accent` on the settings doc; the admin can
 * swap presets (BLACK FRIDAY, CHRISTMAS, SUMMER, HALLOWEEN, NEON) which
 * pre-fill all editable fields, then override anything.
 *
 * Surfaces rendered:
 *   - emoji + label + tagline
 *   - optional promo code chip
 *   - optional CTA button → cta_url (internal path or https://)
 *   - optional "Ends MMM DD" footer
 */
const ACCENT_PALETTES = {
  violet: {
    border: "border-[#14b8a6]/60",
    grad: "from-[#1b073a] via-[#2a0a48] to-[#1b073a]",
    glow1: "rgba(20, 184, 166,0.5)",
    glow2: "rgba(20, 184, 166,0.4)",
    label: "text-[#14b8a6]",
    chip: "bg-black/40 text-[#14b8a6] border-[#14b8a6]/40",
    btn: "bg-gradient-to-r from-[#14b8a6] to-[#14b8a6] text-black",
  },
  red: {
    border: "border-[#ff3a3a]/70",
    grad: "from-[#1a0202] via-[#3a0606] to-[#1a0202]",
    glow1: "rgba(255,58,58,0.55)",
    glow2: "rgba(255,140,0,0.4)",
    label: "text-[#14b8a6]",
    chip: "bg-black/40 text-[#ffe066] border-[#ffe066]/50",
    btn: "bg-gradient-to-r from-[#ff3a3a] to-[#ff8a3a] text-black",
  },
  green: {
    border: "border-[#14b8a6]/60",
    grad: "from-[#04221b] via-[#073a2a] to-[#04221b]",
    glow1: "rgba(20, 184, 166,0.45)",
    glow2: "rgba(20, 184, 166,0.3)",
    label: "text-[#14b8a6]",
    chip: "bg-black/40 text-[#ffe066] border-[#ffe066]/50",
    btn: "bg-gradient-to-r from-[#14b8a6] to-[#7cffcf] text-black",
  },
  amber: {
    border: "border-[#ffc857]/60",
    grad: "from-[#1f1605] via-[#3a280a] to-[#1f1605]",
    glow1: "rgba(255,200,87,0.45)",
    glow2: "rgba(20, 184, 166,0.3)",
    label: "text-[#ffc857]",
    chip: "bg-black/40 text-[#ffe9b8] border-[#ffc857]/40",
    btn: "bg-gradient-to-r from-[#ffc857] to-[#ff8a3a] text-black",
  },
  orange: {
    border: "border-[#ff8a3a]/60",
    grad: "from-[#1b0d04] via-[#3a1a09] to-[#1b0d04]",
    glow1: "rgba(255,138,58,0.45)",
    glow2: "rgba(20, 184, 166,0.3)",
    label: "text-[#ff8a3a]",
    chip: "bg-black/40 text-[#14b8a6] border-[#14b8a6]/40",
    btn: "bg-gradient-to-r from-[#ff8a3a] to-[#ff3a3a] text-black",
  },
  cyan: {
    border: "border-[#14b8a6]/60",
    grad: "from-[#031a26] via-[#073049] to-[#031a26]",
    glow1: "rgba(20, 184, 166,0.45)",
    glow2: "rgba(20, 184, 166,0.3)",
    label: "text-[#14b8a6]",
    chip: "bg-black/40 text-[#14b8a6] border-[#14b8a6]/40",
    btn: "bg-gradient-to-r from-[#14b8a6] to-[#14b8a6] text-black",
  },
  pink: {
    border: "border-[#14b8a6]/60",
    grad: "from-[#220522] via-[#3a073a] to-[#220522]",
    glow1: "rgba(20, 184, 166,0.5)",
    glow2: "rgba(20, 184, 166,0.3)",
    label: "text-[#14b8a6]",
    chip: "bg-black/40 text-[#14b8a6] border-[#14b8a6]/40",
    btn: "bg-gradient-to-r from-[#14b8a6] to-[#ff3a3a] text-black",
  },
};

export function SeasonPassBanner({ override }) {
  const live = useSeasonPassFlag();
  // `override` lets the admin preview without persisting.
  const flag = override || live;
  if (!override && (flag.loading || !flag.enabled)) return null;
  if (override && !override.enabled) return null;

  const palette = ACCENT_PALETTES[(flag.accent || "violet").toLowerCase()] || ACCENT_PALETTES.violet;
  const endsLabel = (() => {
    if (!flag.ends_at) return null;
    try {
      return new Date(flag.ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return null; }
  })();

  return (
    <div data-testid="season-pass-banner"
         className={`rounded-3xl p-4 border-2 ${palette.border} bg-gradient-to-r ${palette.grad} relative overflow-hidden`}>
      <div className="absolute inset-0 opacity-30 pointer-events-none"
           style={{ background: `radial-gradient(circle at 20% 30%, ${palette.glow1}, transparent 60%), radial-gradient(circle at 80% 70%, ${palette.glow2}, transparent 60%)` }} />
      <div className="relative flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <p className={`text-[10px] tracking-[4px] font-bold ${palette.label}`}>
            {(flag.emoji || "⚡") + " "}SEASON PASS · LIVE
          </p>
          <h3 className="font-display text-xl text-white mt-0.5"
              data-testid="season-pass-banner-label">{flag.label}</h3>
          {flag.tagline && (
            <p className="text-[12px] text-[#dceeff] mt-1"
               data-testid="season-pass-banner-tagline">{flag.tagline}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {flag.promo_code && (
              <span data-testid="season-pass-banner-promo"
                    className={`text-[10px] tracking-widest font-bold px-2 py-1 rounded-md border ${palette.chip}`}>
                CODE: {flag.promo_code}
              </span>
            )}
            {endsLabel && (
              <span className="text-[10px] tracking-widest text-white/60">
                · ENDS <span className="text-white/85 font-bold">{endsLabel}</span>
              </span>
            )}
          </div>
        </div>
        {flag.cta_label && flag.cta_url && (
          <a href={flag.cta_url}
             target={flag.cta_url.startsWith("http") ? "_blank" : undefined}
             rel="noreferrer"
             data-testid="season-pass-banner-cta"
             className={`text-[11px] tracking-widest px-4 py-2 rounded-xl font-bold hover:brightness-110 ${palette.btn}`}>
            {flag.cta_label} <i className="fas fa-arrow-right ml-1" />
          </a>
        )}
        {!override && (
          <span className="text-[10px] tracking-widest px-3 py-1.5 rounded-full bg-black/40 text-[#14b8a6] border border-[#14b8a6]/40"
                data-testid="season-pass-banner-state">
            ● ACTIVATED BY ADMIN
          </span>
        )}
      </div>
    </div>
  );
}

export default SeasonPassBanner;
